import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import Layout from '../components/Layout';
import Container from '../components/Container';
import Button from '../components/Button';
import axios from 'axios';

// Modern animations
const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(30px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

// Main page wrapper with modern gradient
const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, 
    #0f172a 0%, 
    #1e293b 25%, 
    #334155 50%, 
    #1e293b 75%, 
    #0f172a 100%
  );
  background-size: 400% 400%;
  animation: gradientShift 15s ease infinite;
  padding: 6rem 0;
  position: relative;
  overflow: hidden;

  @keyframes gradientShift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 0;
  }
`;

// Modern page title with enhanced styling
const PageTitle = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 800;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6, #06b6d4);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientShift 8s ease infinite, ${fadeInUp} 0.8s ease-out;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #3b82f6, #8b5cf6);
    border-radius: 2px;
  }
`;

// Enhanced card styling
const Card = styled.div`
  background: rgba(30, 41, 59, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 24px;
  padding: 2.5rem;
  margin-bottom: 2rem;
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.3),
    0 10px 10px -5px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  animation: ${fadeInUp} 0.8s ease-out;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #3b82f6, transparent);
    animation: ${shimmer} 3s infinite;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(59, 130, 246, 0.4);
    box-shadow: 
      0 25px 50px -12px rgba(0, 0, 0, 0.4),
      0 0 0 1px rgba(59, 130, 246, 0.2);
  }

  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 20px;
  }
`;

const CurrentLicenseInfo = styled(Card)`
  animation-delay: 0.2s;
`;

// Enhanced info items
const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 0;
  border-bottom: 1px solid rgba(59, 130, 246, 0.1);
  transition: all 0.2s ease;
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:hover {
    padding-left: 1rem;
    background: rgba(59, 130, 246, 0.05);
    margin: 0 -1rem;
    border-radius: 12px;
  }
`;

const InfoLabel = styled.span`
  color: #94a3b8;
  font-weight: 500;
  font-size: 1rem;
`;

const InfoValue = styled.span`
  color: #f1f5f9;
  font-weight: 700;
  font-size: 1.1rem;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

// Modern plan options grid
const PlanOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

// Enhanced plan option cards
const PlanOption = styled(Card)`
  cursor: pointer;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid ${props => props.selected ? '#3b82f6' : 'rgba(59, 130, 246, 0.2)'};
  animation-delay: ${props => props.index * 0.1}s;

  &.featured {
    border: 2px solid #8b5cf6;
    background: rgba(139, 92, 246, 0.1);

    &::before {
      content: 'Most Popular';
      position: absolute;
      top: -15px;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, #8b5cf6, #3b82f6);
      color: white;
      padding: 0.5rem 1.5rem;
      border-radius: 20px;
      font-size: 0.875rem;
      font-weight: 700;
      box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.3);
    }
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    border-color: ${props => props.selected ? '#3b82f6' : '#8b5cf6'};
  }

  ${props => props.selected && css`
    animation: ${pulse} 2s infinite;
    border-color: #3b82f6;
    box-shadow: 
      0 25px 50px -12px rgba(59, 130, 246, 0.4),
      0 0 0 1px rgba(59, 130, 246, 0.2);
  `}
`;

const PlanTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const PlanPrice = styled.div`
  font-size: 3rem;
  font-weight: 900;
  color: #f1f5f9;
  margin-bottom: 1.5rem;
  
  span {
    font-size: 1.2rem;
    color: #94a3b8;
    font-weight: 500;
  }
`;

const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
  
  li {
    padding: 0.75rem 0;
    color: #cbd5e1;
    display: flex;
    align-items: center;
    font-size: 1rem;
    transition: all 0.2s ease;
    
    &:hover {
      color: #f1f5f9;
      transform: translateX(5px);
    }
    
    &::before {
      content: '✓';
      color: #22c55e;
      margin-right: 0.75rem;
      font-weight: bold;
      font-size: 1.2rem;
      background: rgba(34, 197, 94, 0.1);
      border-radius: 50%;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

// Modern crypto options
const CryptoOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1.5rem;
  margin: 3rem 0;
`;

const USDTOptions = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin: 1.5rem 0;
  flex-wrap: wrap;
`;

const CryptoOption = styled(Card)`
  padding: 2rem 1.5rem;
  cursor: pointer;
  text-align: center;
  border: 2px solid ${props => props.selected ? '#3b82f6' : 'rgba(59, 130, 246, 0.2)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-5px) scale(1.05);
    border-color: #8b5cf6;
  }
  
  .icon {
    font-size: 2.5rem;
    font-weight: 900;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #3b82f6, #8b5cf6);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .name {
    font-size: 1rem;
    color: #cbd5e1;
    font-weight: 600;
  }

  ${props => props.selected && css`
    animation: ${pulse} 2s infinite;
    border-color: #3b82f6;
    box-shadow: 
      0 25px 50px -12px rgba(59, 130, 246, 0.4),
      0 0 0 1px rgba(59, 130, 246, 0.2);
  `}
`;

const PaymentSummary = styled(Card)`
  animation-delay: 0.4s;
  
  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 2rem;
    background: linear-gradient(135deg, #3b82f6, #8b5cf6);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 0;
  border-bottom: 1px solid rgba(59, 130, 246, 0.1);
  transition: all 0.2s ease;
  
  &:last-child {
    border-bottom: none;
    font-weight: 700;
    font-size: 1.1rem;
  }

  &:hover {
    padding-left: 1rem;
    background: rgba(59, 130, 246, 0.05);
    margin: 0 -1rem;
    border-radius: 12px;
  }
  
  span:first-child {
    color: #94a3b8;
    font-weight: 500;
  }
  
  span:last-child {
    font-weight: 700;
    color: #f1f5f9;
  }
`;

// Modern button container
const ButtonContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  margin-top: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

// Enhanced error message
const ErrorMessage = styled.div`
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.1));
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #fecaca;
  padding: 1.5rem;
  border-radius: 16px;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 600;
  backdrop-filter: blur(10px);
  animation: ${fadeInUp} 0.5s ease-out;
`;

// Modern loading spinner
const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  gap: 2rem;
  
  &::after {
    content: '';
    width: 60px;
    height: 60px;
    border: 4px solid rgba(59, 130, 246, 0.2);
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  &::before {
    content: 'Loading license information...';
    color: #94a3b8;
    font-size: 1.1rem;
    font-weight: 500;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Early access banner
const EarlyAccessBanner = styled(Card)`
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(59, 130, 246, 0.2));
  border: 2px solid #8b5cf6;
  text-align: center;
  animation-delay: 0.3s;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    animation: ${shimmer} 3s infinite;
  }

  .countdown {
    font-size: 1.2rem;
    font-weight: 700;
    color: #fbbf24;
    margin-bottom: 1rem;
    background: rgba(251, 191, 36, 0.1);
    padding: 0.5rem 1rem;
    border-radius: 12px;
    display: inline-block;
  }

  h3 {
    font-size: 2rem;
    font-weight: 800;
    margin: 1rem 0;
    background: linear-gradient(135deg, #8b5cf6, #3b82f6);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .price {
    font-size: 2.5rem;
    font-weight: 900;
    color: #22c55e;
    margin: 1rem 0;
  }

  p {
    font-size: 1.1rem;
    color: #cbd5e1;
    margin-bottom: 2rem;
    line-height: 1.6;
  }
`;

// Reuse the same plans array from BuyPage
const plans = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 60,
    features: [
      '1 month access to AccountWarmer',
      'Full browser profile warming for Amazon, eBay, and Vinted',
      'Access to all automation features',
      'Natural browsing pattern simulation',
      'Cookie and session management',
      'Detailed activity logs',
      'AdsPower integration',
      'Standard customer support',
      'Software updates included'
    ],
    featured: false
  },
  {
    id: 'quarterly',
    name: 'Quarterly',
    price: 140,
    features: [
      '3 months access to AccountWarmer',
      'Full browser profile warming for Amazon, eBay, and Vinted',
      'Access to all automation features',
      'Natural browsing pattern simulation',
      'Cookie and session management',
      'Detailed activity logs',
      'AdsPower integration',
      'Priority customer support',
      'Advanced profile management tools',
      'Regular software updates and improvements',
      'Save $40 compared to monthly'
    ],
    featured: true
  },
  {
    id: 'lifetime',
    name: 'Lifetime',
    price: 300,
    earlyAccessPrice: 250,
    features: [
      'Lifetime access to AccountWarmer',
      'Full browser profile warming for Amazon, eBay, and Vinted',
      'Access to all automation features',
      'Natural browsing pattern simulation',
      'Cookie and session management',
      'Detailed activity logs',
      'AdsPower integration',
      'Priority customer support',
      'Advanced profile management tools',
      'All future updates and improvements',
      'One-time payment, no recurring fees'
    ],
    featured: true,
    isEarlyAccess: true
  }
];

// Available cryptocurrencies for renewal
const cryptoOptions = [
  { id: 'ltc', name: 'Litecoin', icon: 'Ł' },
  { id: 'btc', name: 'Bitcoin', icon: '₿' },
  { id: 'eth', name: 'Ethereum', icon: 'Ξ' },
  { id: 'usdttrc20', name: 'USDT (TRC-20)', icon: '₮', note: 'Low fees' },
  { id: 'usdterc20', name: 'USDT (ERC-20)', icon: '₮', note: 'Higher fees' }
];

function RenewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentLicense, setCurrentLicense] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('quarterly');
  const [selectedCrypto, setSelectedCrypto] = useState('ltc');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentEstimate, setPaymentEstimate] = useState(null);
  const [timeLeft, setTimeLeft] = useState({});

  // Get license key from URL or state
  const licenseKey = location.state?.licenseKey || new URLSearchParams(location.search).get('key');

  useEffect(() => {
    if (!licenseKey) {
      setError('No license key provided. Please return to the application and try again.');
      return;
    }

    // Fetch current license info
    const fetchLicenseInfo = async () => {
      try {
        setLoading(true);
        const response = await axios.post('/api/license/validate', { license_key: licenseKey });
        
        if (response.data.success) {
          setCurrentLicense(response.data);
          // Don't pre-select the old plan, let user choose a new one
          setSelectedPlan('quarterly'); // Default to quarterly
        } else {
          setError(response.data.message || 'Failed to fetch license information');
        }
      } catch (err) {
        setError('Error fetching license information. Please try again later.');
        console.error('Error fetching license:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLicenseInfo();
  }, [licenseKey]);

  // Calculate end date for early access offer (2 weeks from now)
  const endDate = new Date('2025-05-11T23:59:59').getTime();
  
  // Update countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = endDate - now;
      
      if (distance < 0) {
        clearInterval(timer);
        setTimeLeft({ expired: true });
      } else {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000),
          expired: false
        });
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [endDate]);

  const proceedToPayment = () => {
    setStep(2);
  };

  const handleCryptoSelect = (cryptoId) => {
    setSelectedCrypto(cryptoId);
  };

  const handlePurchase = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/payments/renew', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          license_key: licenseKey,
          plan_id: selectedPlan,
          crypto_currency: selectedCrypto
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        navigate(`/status/${data.payment.payment_id}`);
      } else {
        setError(data.message || 'Error creating payment. Please try again.');
      }
    } catch (error) {
      console.error('Payment creation error:', error);
      setError('Network error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <PageWrapper>
          <Container>
            <LoadingSpinner />
          </Container>
        </PageWrapper>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <PageWrapper>
          <Container>
            <ErrorMessage>{error}</ErrorMessage>
            <div style={{ textAlign: 'center' }}>
              <Button onClick={() => window.history.back()}>Go Back</Button>
            </div>
          </Container>
        </PageWrapper>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageWrapper>
        <Container>
          <PageTitle>Renew Your License</PageTitle>
          
          {currentLicense && (
            <CurrentLicenseInfo>
              <h3 style={{ 
                fontSize: '1.5rem', 
                fontWeight: '700', 
                marginBottom: '2rem',
                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Current License Information
              </h3>
              <InfoItem>
                <InfoLabel>Plan Type:</InfoLabel>
                <InfoValue>{currentLicense.plan}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Status:</InfoLabel>
                <InfoValue>{currentLicense.is_expired ? 'Expired' : 'Active'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Expiration Date:</InfoLabel>
                <InfoValue>
                  {currentLicense.plan === 'lifetime' 
                    ? 'Never Expires' 
                    : new Date(currentLicense.expires_at).toLocaleDateString()}
                </InfoValue>
              </InfoItem>
            </CurrentLicenseInfo>
          )}

          {/* Reuse the same plan selection UI from BuyPage */}
          {step === 1 ? (
            <div>
              {/* Early Access Banner */}
              {!timeLeft.expired && (
                <EarlyAccessBanner>
                  <div className="countdown">
                    {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m left
                  </div>
                  <h3>Early Access Special</h3>
                  <div className="price">Lifetime License: $250</div>
                  <p>
                    Get lifetime access at our special early access price. 
                    Regular price: $1000 after the promotion ends.
                  </p>
                  <Button 
                    primary
                    onClick={() => {
                      setSelectedPlan('lifetime');
                      proceedToPayment();
                    }}
                  >
                    Get Lifetime Access
                  </Button>
                </EarlyAccessBanner>
              )}

              {/* Plan Selection */}
              <PlanOptions>
                {plans.map((plan, index) => (
                  <PlanOption 
                    key={plan.id}
                    className={plan.featured ? 'featured' : ''}
                    selected={selectedPlan === plan.id}
                    index={index}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                    <PlanTitle>{plan.name}</PlanTitle>
                    <PlanPrice>
                      ${plan.id === 'lifetime' && !timeLeft.expired ? plan.earlyAccessPrice : plan.price}
                      <span>USD</span>
                    </PlanPrice>
                    <PlanFeatures>
                      {plan.features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </PlanFeatures>
                    <Button 
                      primary={plan.featured}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPlan(plan.id);
                        proceedToPayment();
                      }}
                    >
                      Select Plan
                    </Button>
                  </PlanOption>
                ))}
              </PlanOptions>
            </div>
          ) : (
            <div>
              {/* Crypto Selection */}
              <CryptoOptions>
                {cryptoOptions.filter(crypto => ['ltc', 'btc', 'eth'].includes(crypto.id)).map(crypto => (
                  <CryptoOption 
                    key={crypto.id}
                    selected={selectedCrypto === crypto.id}
                    onClick={() => handleCryptoSelect(crypto.id)}
                  >
                    <div className="icon">{crypto.icon}</div>
                    <div className="name">{crypto.name}</div>
                    {crypto.note && <div className="note">{crypto.note}</div>}
                  </CryptoOption>
                ))}
              </CryptoOptions>

              {/* USDT Options - Centered */}
              <USDTOptions>
                {cryptoOptions.filter(crypto => crypto.id.includes('usdt')).map(crypto => (
                  <CryptoOption 
                    key={crypto.id}
                    selected={selectedCrypto === crypto.id}
                    onClick={() => handleCryptoSelect(crypto.id)}
                    style={{ minWidth: '150px', maxWidth: '200px' }}
                  >
                    <div className="icon">{crypto.icon}</div>
                    <div className="name">{crypto.name}</div>
                    {crypto.note && <div className="note">{crypto.note}</div>}
                  </CryptoOption>
                ))}
              </USDTOptions>

              {/* Payment Summary */}
              {paymentEstimate && (
                <PaymentSummary>
                  <h3>Order Summary</h3>
                  <SummaryRow>
                    <span>Plan:</span>
                    <span>{plans.find(p => p.id === selectedPlan).name}</span>
                  </SummaryRow>
                  <SummaryRow>
                    <span>Price (USD):</span>
                    <span>${paymentEstimate.fiat}</span>
                  </SummaryRow>
                  <SummaryRow>
                    <span>Payment Method:</span>
                    <span>{selectedCrypto.toUpperCase()}</span>
                  </SummaryRow>
                </PaymentSummary>
              )}

              {/* Action Buttons */}
              <ButtonContainer>
                <Button 
                  secondary
                  onClick={() => setStep(1)}
                  disabled={loading}
                >
                  Back
                </Button>
                <Button 
                  primary
                  onClick={handlePurchase}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Proceed to Payment'}
                </Button>
              </ButtonContainer>
            </div>
          )}
        </Container>
      </PageWrapper>
    </Layout>
  );
}

export default RenewPage; 